import React, { useState, useEffect } from 'react';
import { servicePillarsAPI } from '../utils/api';
import { Plus, Edit, Save, X } from 'lucide-react';

const ServicePillars = () => {
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    pillar_code: '',
    pillar_name: '',
    description: '',
    active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchPillars();
  }, []);

  const fetchPillars = async () => {
    try {
      const response = await servicePillarsAPI.getAll();
      setPillars(response.data);
    } catch (error) {
      console.error('Error fetching pillars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await servicePillarsAPI.update(editingId, formData);
      } else {
        await servicePillarsAPI.create(formData);
      }
      fetchPillars();
      resetForm();
    } catch (error) {
      alert('Failed to save pillar');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ pillar_code: '', pillar_name: '', description: '', active: true, display_order: 0 });
  };

  const handleEdit = (pillar) => {
    setEditingId(pillar._id);
    setFormData({
      pillar_code: pillar.pillar_code,
      pillar_name: pillar.pillar_name,
      description: pillar.description || '',
      active: pillar.active,
      display_order: pillar.display_order || 0
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Service Pillars</h1>
        <p className="text-slate-500 mt-1">Manage service offerings</p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">{editingId ? 'Edit' : 'Add'} Service Pillar</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
            <input
              type="text"
              value={formData.pillar_code}
              onChange={(e) => setFormData({ ...formData, pillar_code: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              maxLength="5"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.pillar_name}
              onChange={(e) => setFormData({ ...formData, pillar_name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              rows="2"
            />
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            <Save size={18} />
            Save
          </button>
          {editingId && (
            <button
              onClick={resetForm}
              className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              <X size={18} />
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Code</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Name</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Description</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pillars.map((pillar) => (
              <tr key={pillar._id} className="border-t border-slate-100">
                <td className="py-3 px-4 text-sm font-mono text-slate-900">{pillar.pillar_code}</td>
                <td className="py-3 px-4 text-sm text-slate-900">{pillar.pillar_name}</td>
                <td className="py-3 px-4 text-sm text-slate-600">{pillar.description || 'N/A'}</td>
                <td className="py-3 px-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${pillar.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {pillar.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <button onClick={() => handleEdit(pillar)} className="text-sm text-primary hover:text-primary/80">
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ServicePillars;